import React from 'react'
import { Dropdown, SearchableDropdown } from '@entur/dropdown'
import { VariantType } from '@entur/form'
import {
  getEmploymentEndCauses,
  getEmploymentTypes,
  getFIPTicketTypes,
  getPaymentTypes,
} from '../../api-clients/eba-clients'
import { useApiOnLoad } from '../../api-clients/client-hooks'
import {
  AuthenticatedHttpRequest,
  EmploymentEndCause,
  EmploymentType,
  FetchState,
  HttpResponse,
  PayType,
  TicketType,
} from '../../api-clients/api-types'
import { nullValue, ValidationFormField } from '../../utils/form-utils'

export type DropdownItemType = {
  value: string | undefined
  label: string
}

type FetchDropdownProps = {
  id: string
  field: ValidationFormField<string | undefined>
  update: (value: ValidationFormField<string | undefined>) => void
  dataRequest: (request: AuthenticatedHttpRequest) => Promise<HttpResponse<unknown[]>>
  mapResponse: (response: unknown) => DropdownItemType
  noOp?: string
  disabled?: boolean
}

const FetchDropdown: React.FC<FetchDropdownProps> = (props) => {
  const [response] = useApiOnLoad<Array<unknown>>(props.dataRequest, {})
  const undefinedLabel: Array<DropdownItemType> = props.noOp ? [{ label: props.noOp, value: nullValue }] : []
  const selectedItem =
    props.field.value !== undefined && props.field.value !== ''
      ? {
          label: props.field.displayValue || props.noOp || '',
          value: props.field.value,
        }
      : null

  const items =
    response.status === FetchState.SUCCESS
      ? () => response.data.flatMap<DropdownItemType>((item) => props.mapResponse(item)).concat(undefinedLabel)
      : [{ label: props.field.displayValue || props.noOp || '', value: props.field.value }, ...undefinedLabel]

  return (
    <SearchableDropdown<string | undefined>
      style={{ cursor: 'pointer' }}
      label={props.field.displayName}
      variant={props.field.variant as VariantType}
      feedback={props.field.feedback}
      items={items}
      selectedItem={selectedItem}
      disabled={props.disabled}
      onChange={(e) => {
        if (e) {
          props.update({
            ...props.field,
            value: e.value,
            displayValue: e.label === props.noOp ? ' ' : e.label,
          })
        }
      }}
    />
  )
}

export default FetchDropdown

type SpecificDropdownProps = {
  field: ValidationFormField<string | undefined>
  update: (value: ValidationFormField<string | undefined>) => void
}

export const EmploymentTypeDropdown: React.FC<SpecificDropdownProps> = ({ field, update }) => {
  return (
    <FetchDropdown
      id="deploymentTypeDropdown"
      dataRequest={getEmploymentTypes}
      mapResponse={(response) => {
        return {
          label: (response as EmploymentType).description,
          value: (response as EmploymentType).code,
        }
      }}
      field={field}
      update={update}
    />
  )
}

export const PaymentTypeDropdown: React.FC<SpecificDropdownProps> = ({ field, update }) => {
  return (
    <FetchDropdown
      id="paymentTypeDropdown"
      dataRequest={getPaymentTypes}
      mapResponse={(response) => {
        const payType = response as PayType
        return { label: `${payType.code} - ${payType.description}`, value: payType.code }
      }}
      field={field}
      update={update}
    />
  )
}

export const EmploymentEndCauseDropdown: React.FC<SpecificDropdownProps> = ({ field, update }) => {
  return (
    <FetchDropdown
      id="employmentEndCauseDropdown"
      dataRequest={getEmploymentEndCauses}
      mapResponse={(response) => {
        const employmentEndCause = response as EmploymentEndCause
        return {
          label: `${employmentEndCause.code} - ${employmentEndCause.description}`,
          value: employmentEndCause.code,
        }
      }}
      field={field}
      update={update}
      noOp="Ingen sluttårsak"
    />
  )
}

type FIPTicketTypeDropdownProps = {
  field: ValidationFormField<string | undefined>
  update: (value: ValidationFormField<string | undefined>) => void
  disabled: boolean
}
export const FIPTicketTypeDropdown: React.FC<FIPTicketTypeDropdownProps> = (props) => {
  return (
    <FetchDropdown
      id="FIPTicketTypeDropdown"
      dataRequest={(request: AuthenticatedHttpRequest) => {
        return getFIPTicketTypes(request).then((r) => {
          if (r.contentType === 'json') {
            return {
              contentType: 'json',
              json: r.json.items
                .filter((it) => it.countryCode !== 'N')
                .sort((a, b) => {
                  return a.code.localeCompare(b.code)
                }),
            }
          }
          throw new Error('Response is not what expected')
        })
      }}
      mapResponse={(response) => {
        const ticketType = response as TicketType
        return {
          label: `${ticketType.code} - ${ticketType.description}`,
          value: ticketType.id.toString(),
        }
      }}
      field={props.field}
      update={props.update}
      disabled={props.disabled}
    />
  )
}

export type EmployeeTicketType = {
  field: ValidationFormField<string>
  update: (value: ValidationFormField<string>) => void
  items: DropdownItemType[]
  disabled: boolean
}
export const TicketTypeDropdown: React.FC<EmployeeTicketType> = (props) => {
  const selectedItem =
    props.field.value !== ''
      ? {
          label: props.field.displayValue || '',
          value: props.field.value,
        }
      : null

  return (
    <Dropdown<string>
      style={{ cursor: 'pointer' }}
      label={props.field.displayName}
      variant={props.field.variant as VariantType}
      feedback={props.field.feedback}
      items={props.items}
      selectedItem={selectedItem}
      readOnly={props.disabled}
      labelTooltip={props.disabled ? 'Rettighetseier må ha en rettighet synkronisert til ESS.' : undefined}
      onChange={(e) => {
        if (e) {
          props.update({ ...props.field, value: e.value, displayValue: e.label })
        }
      }}
    />
  )
}
